import { useEffect, useState } from "react";
import { FileText, X } from "lucide-react";
import { Button } from "@/components/ui";

interface DemoFileMetadata {
  name: string;
  description: string;
  size: number;
  fileType: string;
}

interface DemoFilePickerProps {
  open: boolean;
  onClose: () => void;
  onFilesSelected: (files: File[]) => void;
}

export function DemoFilePicker({
  open,
  onClose,
  onFilesSelected,
}: DemoFilePickerProps) {
  const [files, setFiles] = useState<Record<string, DemoFileMetadata>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch metadata on mount
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    fetch("/demo-files/index.json")
      .then((res) => res.json())
      .then((metadata) => {
        setFiles(metadata);
      })
      .catch((err) => {
        console.error("Failed to load demo files metadata:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const handleToggle = (fileName: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(fileName)) {
      newSelected.delete(fileName);
    } else {
      newSelected.add(fileName);
    }
    setSelected(newSelected);
  };

  const handleConfirm = async () => {
    const filesToAdd: File[] = [];

    for (const fileName of selected) {
      try {
        const response = await fetch(`/demo-files/${fileName}`);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: "application/pdf" });
        filesToAdd.push(file);
      } catch (err) {
        console.error(`Failed to load demo file ${fileName}:`, err);
      }
    }

    if (filesToAdd.length > 0) {
      onFilesSelected(filesToAdd);
    }
    setSelected(new Set());
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-primary">Demo Files</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted">Loading demo files...</p>
            </div>
          ) : Object.keys(files).length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted">No demo files available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(files).map(([fileName, metadata]) => (
                <label
                  key={fileName}
                  className="flex items-start gap-3 p-4 border rounded-lg hover:bg-page/50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(fileName)}
                    onChange={() => handleToggle(fileName)}
                    className="mt-1 w-4 h-4 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText
                        size={16}
                        className="text-accent flex-shrink-0"
                      />
                      <p className="font-medium text-primary truncate">
                        {metadata.name}
                      </p>
                    </div>
                    <p className="text-sm text-muted mt-1 line-clamp-2">
                      {metadata.description}
                    </p>
                    <p className="text-xs text-muted/60 mt-2">
                      {(metadata.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={selected.size === 0 || loading}
          >
            Add{" "}
            {selected.size > 0
              ? `${selected.size} File${selected.size !== 1 ? "s" : ""}`
              : "Files"}
          </Button>
        </div>
      </div>
    </div>
  );
}
